/**
 * AskSLAMAI - Modern AI Chat Interface with Canvas
 *
 * A lightweight, Claude/Gemini/ChatGPT-style chat interface featuring:
 * - Split-panel layout (Chat + Canvas)
 * - Inline agent trace visualization
 * - AG-UI widget rendering
 * - SSE streaming with real-time updates
 * - Artifact management in canvas panel
 *
 * This is the main chat component for Ask SLAM AI.
 */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Send,
  Bot,
  User,
  Sparkles,
  RefreshCw,
  Zap,
  PanelRightOpen,
  PanelRightClose,
  Loader2,
  Globe,
  Download,
  Paperclip,
  X,
  FileVideo,
  FileText,
  Image,
  Music,
  File,
  BarChart2,
  Clock,
  Plus,
  ChevronDown,
  Trash2,
  Search,
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';

// Local imports
import useAgentChat from '../../hooks/useAgentChat';
import { useAnalyticsChat } from '../../hooks/useAnalyticsChat';
import AgentTraceInline from './AgentTraceInline';
import PatternLegend from './PatternLegend';
import ChatCanvas from './ChatCanvas';
import MCPAppRenderer from './MCPAppRenderer';
import { WidgetRenderer } from './widgets/AgentWidgets';
import { CitationSuperscript } from './CitationSuperscript';
import { SourceDrawer } from './SourceDrawer';
import { FreshnessSummary } from './FreshnessBadge';
import { useTeam } from '../../lib/teamConfig';
import { getSportSpecificQuestions } from '../../utils/constant';
import {
  buildChatSportContext,
  buildChatContextPayload,
} from '../../lib/chat/sportContext';
import useTranslation from '../../hooks/useTranslation';
import { C } from '../design-system/primitives/constants';
import { exportToPDF, generatePDFFilename } from '../../lib/pdfService';
import {
  uploadFileToGCS,
  uploadFileViaBackend,
  submitVideoAnalysis,
  updateVideoAnalysis,
  getUser,
  getSelectedSport,
} from '../../utils/helpers';

// ── Gemini-supported MIME types ─────────────────────────────────────────────
// Full list of file types Google Gemini / Vertex AI can process for extraction
const GEMINI_ACCEPT_TYPES = [
  // Video
  'video/mp4', 'video/mpeg', 'video/mov', 'video/quicktime', 'video/avi',
  'video/x-flv', 'video/mpg', 'video/webm', 'video/wmv', 'video/3gpp',
  // Image
  'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/heic', 'image/heif',
  // Audio
  'audio/wav', 'audio/mp3', 'audio/mpeg', 'audio/aiff', 'audio/aac',
  'audio/ogg', 'audio/flac', 'audio/x-wav',
  // Documents
  'application/pdf', 'text/plain', 'text/csv', 'text/html', 'text/xml',
  'text/css', 'text/javascript', 'text/x-python', 'text/markdown',
  // Structured data
  'application/json', 'application/xml',
  // Spreadsheets
  '.xlsx', '.xls', '.csv',
  // Common file extensions as fallbacks
  '.txt', '.md', '.pdf', '.py', '.js', '.ts', '.html', '.xml', '.json',
].join(',');

/**
 * Classify a file into a content category for routing to the appropriate analysis pipeline.
 * Returns: 'video' | 'image' | 'audio' | 'document' | 'data' | 'code' | 'unknown'
 */
function classifyFileType(file) {
  const mime = (file.type || '').toLowerCase();
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';

  // Video
  if (mime.startsWith('video/') || ['mp4', 'mov', 'avi', 'webm', 'mkv', 'wmv', 'flv', 'mpg', 'mpeg', '3gp'].includes(ext)) {
    return 'video';
  }
  // Image
  if (mime.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif', 'bmp', 'svg'].includes(ext)) {
    return 'image';
  }
  // Audio
  if (mime.startsWith('audio/') || ['wav', 'mp3', 'aiff', 'aac', 'ogg', 'flac', 'm4a', 'wma'].includes(ext)) {
    return 'audio';
  }
  // Structured data (CSV, JSON, XML, XLSX)
  if (['csv', 'json', 'xml', 'xlsx', 'xls', 'tsv'].includes(ext) ||
      ['text/csv', 'application/json', 'application/xml', 'text/xml'].includes(mime)) {
    return 'data';
  }
  // Documents (PDF, TXT, MD, HTML)
  if (['pdf', 'txt', 'md', 'html', 'htm', 'rtf', 'doc', 'docx'].includes(ext) ||
      ['application/pdf', 'text/plain', 'text/html', 'text/markdown'].includes(mime)) {
    return 'document';
  }
  // Code
  if (['py', 'js', 'ts', 'jsx', 'tsx', 'java', 'cpp', 'c', 'go', 'rs', 'rb', 'php', 'swift', 'kt'].includes(ext) ||
      mime.startsWith('text/x-')) {
    return 'code';
  }
  return 'unknown';
}

/**
 * Get the appropriate icon component for a file category
 */
function getFileIcon(category) {
  switch (category) {
    case 'video': return FileVideo;
    case 'image': return Image;
    case 'audio': return Music;
    case 'data': return FileText;
    case 'document': return FileText;
    case 'code': return File;
    default: return File;
  }
}

// Default sample prompts (can be overridden by sport-specific props)
const DEFAULT_PROMPTS = [
  { id: 1, text: 'Analyze recent performance trends', icon: '📊' },
  { id: 2, text: 'Generate a scouting report', icon: '🔍' },
  { id: 3, text: 'Compare key metrics', icon: '📈' },
  { id: 4, text: 'What are the best strategies?', icon: '🎯' },
];

const BASKETBALL_NL2SQL_HINTS = [
  'segment',
  'possession',
  'play type',
  'risk',
  'biomech',
  'wembanyama',
  'wemby',
  'holmgren',
  'sga',
  'spurs',
  'thunder',
  'okc',
  'shot chart',
  'pick-and-roll',
  'pick and roll',
  'closeout',
  'box score',
  'drive',
  'momentum',
];

function hasAnyHint(text, hints) {
  const normalized = (text || '').toLowerCase();
  return hints.some((hint) => normalized.includes(hint));
}

function shouldUseBasketballNl2Sql(queryText, chatContext) {
  if (!chatContext?.isBasketball) {
    return false;
  }

  // Only route to NL2SQL when the query explicitly references video analysis
  // data (Spurs-OKC segments, possessions, biomechanics, etc.).
  // General basketball queries should go to /api/chat/context which has
  // NatStat tools for league-wide stats across all teams.
  const normalized = (queryText || '').toLowerCase();
  return hasAnyHint(normalized, BASKETBALL_NL2SQL_HINTS);
}

/**
 * MessageBubble - Individual chat message with optional trace
 */
const MessageBubble = ({
  message,
  showTrace = true,
  onCitationClick,
}) => {
  const isUser = message.role === 'user';

  return (
    <div className={`flex gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}>
      {/* Avatar */}
      <div
        className="flex-shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
        style={{ background: isUser ? C.g15 : C.g08, color: C.gold }}
      >
        {isUser ? <User className="w-3 h-3" /> : <Bot className="w-3 h-3" />}
      </div>

      {/* Content */}
      <div className={`flex-1 max-w-[85%] ${isUser ? 'text-right' : 'text-left'}`}>
        {/* Message bubble */}
        <div
          className="inline-block rounded-xl px-3 py-2 text-xs"
          style={isUser
            ? { background: C.gold, color: C.bg }
            : { background: C.s3, color: C.text, border: `1px solid ${C.borderLight}` }
          }
        >
          {isUser ? (
            <p>{message.content}</p>
          ) : (
            <div
              className="prose prose-xs max-w-none dark:prose-invert [&_p]:text-xs [&_li]:text-xs [&_h1]:text-sm [&_h2]:text-xs [&_h3]:text-xs"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(message.content || '')) }}
            />
          )}
        </div>

        {/* MCP App cards (interactive visualizations inline) */}
        {!isUser && message.artifacts && message.artifacts.filter(a => a.type === 'mcp_app').length > 0 && (
          <div className="mt-2 space-y-2">
            {message.artifacts.filter(a => a.type === 'mcp_app').map((app, i) => (
              <div
                key={`mcp-${i}`}
                className="rounded-xl overflow-hidden"
                style={{ border: `1px solid ${C.goldDim}`, background: C.s1 }}
              >
                <div
                  className="flex items-center gap-2 px-3 py-1.5"
                  style={{
                    background: `linear-gradient(135deg, ${C.s2} 0%, ${C.s1} 100%)`,
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  <span
                    className="w-5 h-5 rounded flex items-center justify-center"
                    style={{ background: C.g08, color: C.gold, fontSize: 10 }}
                  >
                    ◈
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>
                    {app.title || 'Interactive View'}
                  </span>
                  <span
                    className="ml-auto"
                    style={{
                      fontSize: 9, fontWeight: 600, padding: '1px 5px',
                      borderRadius: 4, background: C.g08, color: C.gold,
                      textTransform: 'uppercase', letterSpacing: 0.5,
                    }}
                  >
                    MCP APP
                  </span>
                </div>
                <MCPAppRenderer
                  resourceUri={app.resourceUri}
                  toolName={app.toolName}
                  toolInput={app.toolInput || {}}
                  toolResult={app.toolResult || {}}
                  height={380}
                />
              </div>
            ))}
          </div>
        )}

        {/* Citations and freshness indicators removed — clean reasoning UI */}

        {/* Inline widgets */}
        {message.widgets && message.widgets.length > 0 && (
          <div className="mt-2 space-y-2">
            {message.widgets.map((widget, idx) => (
              <WidgetRenderer key={widget.widgetId || idx} widget={widget} />
            ))}
          </div>
        )}

        {/* Inline trace (for assistant messages - always visible) */}
        {!isUser && message.traceSteps && message.traceSteps.length > 0 && (
          <AgentTraceInline
            steps={message.traceSteps}
            agentState={message.agentState || { status: 'complete' }}
            className="mt-2 text-left"
          />
        )}
      </div>
    </div>
  );
};

/**
 * Main AskSLAMAI Component
 */
export default function AskSLAMAI({
  sport = 'auto',
  prompts = DEFAULT_PROMPTS,
  endpoint = null,
  systemContext = '',
  // Optional overrides for embedded contexts (e.g., UNC GM Dashboard)
  sportCategory: propSportCategory,
  leagueCode: propLeagueCode,
  teamName: propTeamName,
  conference: propConference,
}) {
  // Team context
  const {
    config: teamConfig,
    selectedTeam,
    selectedSport,
    sportCode,
  } = useTeam();

  // Translation / multilingual support
  const { language, setLanguage, t, supportedLanguages } = useTranslation();

  const sportHint = sport === 'auto' ? selectedSport : sport;
  const chatContext = useMemo(() => {
    const base = buildChatSportContext({
      selectedTeam,
      selectedSport,
      sportCode,
      teamConfig,
      sportHint,
    });
    // Apply prop overrides for embedded contexts (UNC GM Dashboard, etc.)
    if (propLeagueCode) base.leagueCode = propLeagueCode;
    if (propSportCategory) base.sportCategory = propSportCategory;
    if (propTeamName) base.teamName = propTeamName;
    if (propConference) base.conference = propConference;
    return base;
  }, [selectedTeam, selectedSport, sportCode, teamConfig, sportHint, propSportCategory, propLeagueCode, propTeamName, propConference]);

  const effectiveSport = chatContext.sportCategory || 'baseball';
  const effectiveSportCode = chatContext.leagueCode || chatContext.sportCode || 'baseball';
  const sportPromptDefaults = useMemo(
    () => getSportSpecificQuestions(effectiveSportCode)
      .slice(0, 6)
      .map((entry, index) => ({
        id: entry.id || index + 1,
        text: entry.text || entry.question || String(entry),
        icon: entry.icon || '📊',
      })),
    [effectiveSportCode],
  );
  const effectivePrompts = prompts === DEFAULT_PROMPTS
    ? sportPromptDefaults
    : prompts;
  const resolvedEndpoint = endpoint || '/api/agent/chat';

  // Team info
  const teamName = chatContext.teamName || teamConfig?.name || 'SLAM Sports';

  // Chat state
  const [input, setInput] = useState('');
  const [sessionId] = useState(() => uuidv4());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // UI state
  const [showCanvas, setShowCanvas] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [useAgentMode, setUseAgentMode] = useState(true);
  const [selectedModel, setSelectedModel] = useState('gemini'); // 'gemini' | 'claude'

  // Analytics mode state
  const [analyticsMode, setAnalyticsMode] = useState(false);
  const [showSessionPicker, setShowSessionPicker] = useState(false);
  const [sessionSearch, setSessionSearch] = useState('');
  const [showPatternLegend, setShowPatternLegend] = useState(false);

  // Analytics chat hook (only active when analyticsMode is true)
  const userId = useMemo(() => {
    try { const u = getUser(); return u?.id || 'anon'; } catch { return 'anon'; }
  }, []);
  const analytics = useAnalyticsChat({
    userId,
    sportContext: chatContext,
  });

  // File upload state
  const fileInputRef = useRef(null);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Source drawer state (for citations)
  const [drawerCitations, setDrawerCitations] = useState(null);
  const [showSourceDrawer, setShowSourceDrawer] = useState(false);
  const handleCitationClick = useCallback((citations) => {
    setDrawerCitations(citations);
    setShowSourceDrawer(true);
  }, []);

  // Top 8 languages for quick access, rest available via expansion
  const TOP_LANGUAGES = useMemo(() =>
    supportedLanguages.filter(l => ['en', 'es', 'fr', 'de', 'ja', 'ko', 'zh', 'pt'].includes(l.code)),
    [supportedLanguages]
  );
  const [showAllLanguages, setShowAllLanguages] = useState(false);

  // Agent chat hook - use messages directly from hook
  // Wire analytics onTurnComplete when analyticsMode is active
  const {
    sendMessage,
    cancel,
    reset: resetChat,
    isLoading,
    error,
    messages, // Use messages from hook directly
    textContent,
    agentState,
    statusMessage,
    currentAgentName,
    traceSteps,
    widgets,
    artifacts,
    toggleArtifactPin,
    removeArtifact,
    setMessages,
  } = useAgentChat({
    endpoint: resolvedEndpoint,
    sport: effectiveSport,
    onArtifact: () => setShowCanvas(true), // Auto-open canvas when artifact is generated
    onTurnComplete: analyticsMode ? analytics.onTurnComplete : null,
  });

  // Configure marked
  useEffect(() => {
    marked.setOptions({ breaks: true, gfm: true });
  }, []);

  // Restore chat messages when a past analytics session is loaded (CR1)
  useEffect(() => {
    if (!analytics.loadedTurns || analytics.loadedTurns.length === 0) return;
    const restored = analytics.loadedTurns.map((turn, i) => ({
      id: turn.turn_id || `restored-${i}`,
      role: turn.role,
      content: turn.content || '',
    }));
    setMessages(restored);
  }, [analytics.loadedTurns, setMessages]);

  // Scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, textContent]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLanguageDropdown && !event.target.closest('.language-dropdown')) {
        setShowLanguageDropdown(false);
        setShowAllLanguages(false);
      }
      if (showSessionPicker && !event.target.closest('.session-picker-dropdown')) {
        setShowSessionPicker(false);
        setSessionSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown, showSessionPicker]);

  // Send message - hook manages messages state
  const handleSendMessage = useCallback(async (messageText = input) => {
    if (!messageText.trim() && attachedFiles.length === 0) return;
    if (isLoading) return;

    // Build default message if only files attached
    const fileCategories = attachedFiles.map(f => f.category || classifyFileType(f));
    const primaryCategory = fileCategories[0] || 'file';
    const defaultFileMsg = attachedFiles.length > 0
      ? `Analyze ${primaryCategory === 'video' ? 'this video' : primaryCategory === 'image' ? 'this image' : primaryCategory === 'data' ? 'this data' : primaryCategory === 'document' ? 'this document' : primaryCategory === 'audio' ? 'this audio' : 'these files'}: ${attachedFiles.map(f => f.name).join(', ')}`
      : '';

    const message = (messageText || '').trim() || defaultFileMsg;
    setInput('');

    const currentFiles = [...attachedFiles];
    setAttachedFiles([]);

    // Classify files by category for appropriate upload/routing
    const videoFiles = currentFiles.filter(f => classifyFileType(f) === 'video');
    const mediaFiles = currentFiles.filter(f => ['image', 'audio'].includes(classifyFileType(f)));
    const otherFiles = currentFiles.filter(f => !['video', 'image', 'audio'].includes(classifyFileType(f)));
    const hasVideo = videoFiles.length > 0;
    const hasMedia = mediaFiles.length > 0;

    // Upload video and media files to GCS (they need cloud storage for Gemini processing)
    let uploadedFileUris = [];
    const filesToUpload = [...videoFiles, ...mediaFiles];
    if (filesToUpload.length > 0 || otherFiles.length > 0) {
      setIsUploading(true);
    }

    // Backend-mediated upload for data/document/code files: POST to /api/chat/upload-file
    // (multer -> GCS). Returns gs_uri that the analyze_file tool can consume.
    for (const of of otherFiles) {
      const fileCategory = classifyFileType(of);
      try {
        const { gsUri, mimeType, displayName, inlineBase64 } = await uploadFileViaBackend(of.file);
        uploadedFileUris.push({
          name: displayName || of.name,
          size: of.size,
          mimeType: mimeType || of.type,
          category: fileCategory,
          gsUri,
          inlineBase64: gsUri ? null : inlineBase64,
          uploadFailed: !gsUri && !inlineBase64,
        });
      } catch (uploadErr) {
        console.error('[AskSLAMAI] Backend upload failed for', of.name, uploadErr);
        uploadedFileUris.push({
          name: of.name,
          size: of.size,
          mimeType: of.type,
          category: fileCategory,
          gsUri: null,
          uploadFailed: true,
        });
      }
    }

    if (filesToUpload.length > 0) {
      for (const vf of filesToUpload) {
        const user = getUser();
        const sport = getSelectedSport() || chatContext.sportCategory || 'general';
        const fileCategory = classifyFileType(vf);

        // 1. Create Spanner record (for video files only — images/audio don't need analysis records)
        let analysisId = null;
        if (fileCategory === 'video') {
          try {
            const submitResult = await submitVideoAnalysis({
              player_id: '',
              response: '',
              user_id: user?.id,
              video_name: vf.name,
              sport,
              size: vf.size || 0,
              source_type: 'gcs',
              source_url: '',
              ingestion_method: 'chat_upload',
            });
            analysisId = submitResult?.id;
          } catch (spannerErr) {
            console.warn('[AskSLAMAI] Spanner record creation failed, proceeding with upload:', spannerErr.message);
          }
        }

        // 2. Upload to GCS (proceed even if Spanner record failed)
        try {
          const gsUrl = await uploadFileToGCS(vf.file, {
            Sports: sport,
            UserID: user?.id,
            VideoID: analysisId || `media-${Date.now()}`,
            is_analysis: fileCategory === 'video',
          });

          // 3. Update Spanner record with GCS URI (video only, if record was created)
          if (analysisId && gsUrl) {
            try {
              await updateVideoAnalysis({ id: analysisId, gs_uri: gsUrl });
            } catch (updateErr) {
              console.warn('[AskSLAMAI] Spanner update failed:', updateErr.message);
            }
          }

          uploadedFileUris.push({
            name: vf.name,
            size: vf.size,
            mimeType: vf.type,
            category: fileCategory,
            gsUri: gsUrl,
            analysisId,
          });
        } catch (uploadErr) {
          console.error('[AskSLAMAI] GCS upload failed for', vf.name, uploadErr);
          // Still include file metadata so backend knows a file was intended
          uploadedFileUris.push({
            name: vf.name,
            size: vf.size,
            mimeType: vf.type,
            category: fileCategory,
            gsUri: null,
            analysisId,
            uploadFailed: true,
          });
        }
      }
    }
    setIsUploading(false);

    // All files (video/image/audio + data/document/code) now carry gs_uri when upload succeeded.
    const files = uploadedFileUris;

    try {
      const contextPayload = buildChatContextPayload(chatContext);
      const useNl2sql = shouldUseBasketballNl2Sql(message, chatContext);

      // Primary video URI for the backend analyze_video tool
      const videoUri = uploadedFileUris.find(f => f.category === 'video')?.gsUri || null;

      // Pass model selection to backend ('gemini-3.1-pro-preview' default, 'claude-opus' for Claude)
      const modelOverride = selectedModel === 'claude' ? 'claude-opus' : undefined;

      await sendMessage(message, {
        sessionId,
        threadId: sessionId,
        endpointOverride: useNl2sql ? '/api/basketball/nl2sql' : resolvedEndpoint,
        contextPayload,
        language,
        properties: {
          ...contextPayload,
          isBaseball: chatContext.isBaseball,
          nl2sqlScope: useNl2sql ? 'sport_selector' : null,
          files,
          hasVideoAttachment: hasVideo,
          hasMediaAttachment: hasMedia,
          videoUri,
          model: modelOverride,
        },
      });
    } catch (err) {
      console.error('Send message error:', err);
    }
  }, [input, isLoading, sendMessage, sessionId, chatContext, resolvedEndpoint, language, attachedFiles, selectedModel]);

  // Handle prompt click
  const handlePromptClick = (prompt) => {
    handleSendMessage(prompt.text);
  };

  // Clear chat
  const handleClearChat = () => {
    resetChat();
    setShowCanvas(false);
    setAttachedFiles([]);
  };

  // File attachment handler
  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const fileInfos = files.map(f => {
      const category = classifyFileType(f);
      return {
        file: f,
        name: f.name,
        size: f.size,
        type: f.type,
        category, // 'video' | 'image' | 'audio' | 'document' | 'data' | 'code'
        preview: category === 'video' ? URL.createObjectURL(f) : null,
      };
    });
    setAttachedFiles(prev => [...prev, ...fileInfos]);
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const removeAttachedFile = useCallback((index) => {
    setAttachedFiles(prev => {
      const removed = prev[index];
      if (removed?.preview) URL.revokeObjectURL(removed.preview);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // ── Export conversation + artifacts to PDF ────────────────────────────────
  const [isExporting, setIsExporting] = useState(false);

  const handleExportConversation = useCallback(async () => {
    if (messages.length === 0) return;
    setIsExporting(true);
    try {
      // Serialize conversation to markdown
      let md = `# Ask SLAM AI — Conversation Report\n\n`;
      md += `**Team:** ${teamName}  \n`;
      md += `**Sport:** ${chatContext.isBaseball ? 'Baseball' : 'Basketball'}`;
      if (chatContext.leagueCode) md += ` (${chatContext.leagueCode})`;
      md += `  \n`;
      md += `**Date:** ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}  \n`;
      md += `**Messages:** ${messages.length}  \n\n---\n\n`;

      // Render each message
      for (const msg of messages) {
        const role = msg.role === 'user' ? 'Coach' : 'SLAM AI';
        md += `### ${role}\n\n`;
        md += `${msg.content || ''}\n\n`;

        // Include inline citations summary
        if (msg.citations?.length > 0) {
          md += `*Sources: ${msg.citations.map(c => c.title || c.url).join(', ')}*\n\n`;
        }
      }

      // Serialize canvas artifacts
      if (artifacts?.length > 0) {
        md += `---\n\n## Canvas Artifacts\n\n`;
        for (const art of artifacts) {
          md += `### ${art.title || art.type || 'Artifact'}\n\n`;
          if (art.type === 'data_table' && art.data) {
            const { columns, rows } = art.data;
            if (columns?.length && rows?.length) {
              md += `| ${columns.join(' | ')} |\n`;
              md += `| ${columns.map(() => '---').join(' | ')} |\n`;
              rows.slice(0, 50).forEach(row => {
                const cells = columns.map(col => String(row[col] ?? ''));
                md += `| ${cells.join(' | ')} |\n`;
              });
              if (rows.length > 50) md += `\n*...and ${rows.length - 50} more rows*\n`;
              md += '\n';
            }
          } else if (art.type === 'sql_query' && art.data) {
            md += `**Query:** ${art.data.naturalLanguage || ''}\n\n`;
            md += `\`\`\`sql\n${art.data.generatedSQL || ''}\n\`\`\`\n\n`;
          } else if (art.type === 'strategy' && art.data) {
            if (art.data.summary) md += `${art.data.summary}\n\n`;
            (art.data.recommendations || []).forEach((rec, i) => {
              md += `${i + 1}. **${rec.title || ''}** — ${rec.description || ''}\n`;
            });
            md += '\n';
          } else if (art.type === 'chart' && art.data) {
            md += `*Chart: ${art.data.title || art.data.chartType || 'visualization'}*\n\n`;
          } else if (art.type === 'video_report' && art.data) {
            md += `${art.data.analysis || JSON.stringify(art.data, null, 2)}\n\n`;
          }
        }
      }

      md += `\n---\n*Report generated by SLAM Sports AI*\n`;

      const filename = generatePDFFilename('slam-ai-conversation');
      await exportToPDF(md, { filename, team: teamName });
    } catch (err) {
      console.error('[AskSLAMAI] Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  }, [messages, artifacts, teamName, chatContext]);

  // Render the input bar (reused in both empty state and messages view)
  const renderInputBar = () => (
    <div className="w-full">
      {/* Attached files preview */}
      {attachedFiles.length > 0 && (
        <div className="flex gap-2 mb-1.5 flex-wrap">
          {attachedFiles.map((f, i) => {
            const category = f.category || classifyFileType(f);
            const IconComponent = getFileIcon(category);
            const sizeStr = f.size >= 1024 * 1024
              ? `${(f.size / (1024 * 1024)).toFixed(1)}MB`
              : `${Math.round(f.size / 1024)}KB`;
            return (
              <div
                key={i}
                className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px]"
                style={{ background: C.s3, border: `1px solid ${C.border}`, color: C.t2 }}
              >
                <IconComponent className="w-3 h-3" style={{ color: C.gold }} />
                <span className="max-w-[120px] truncate">{f.name}</span>
                <span style={{ color: C.t3 }}>({sizeStr})</span>
                <span className="px-1 rounded" style={{ background: C.g08, color: C.gold, fontSize: 8, textTransform: 'uppercase' }}>{category}</span>
                <button
                  onClick={() => removeAttachedFile(i)}
                  className="ml-0.5 rounded-full p-0.5 hover:opacity-70"
                  style={{ color: C.t3 }}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload progress indicator */}
      {isUploading && (
        <div className="flex items-center gap-2 mb-1.5 px-3 py-1.5 rounded-lg text-[10px]"
          style={{ background: 'rgba(234, 179, 8, 0.08)', border: `1px solid rgba(234, 179, 8, 0.2)`, color: C.gold }}>
          <Loader2 className="w-3 h-3 animate-spin" />
          <span>Uploading files to cloud storage...</span>
        </div>
      )}

      <div className="flex items-center gap-1.5 rounded-xl p-1" style={{ background: C.s2, border: `1px solid ${C.border}` }}>
        {/* File upload button */}
        <input
          ref={fileInputRef}
          type="file"
          accept={GEMINI_ACCEPT_TYPES}
          multiple
          onChange={handleFileSelect}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-2 rounded-lg transition-all"
          style={{ background: 'transparent', color: attachedFiles.length > 0 ? C.gold : C.t3 }}
          title="Attach files (video, images, audio, PDF, CSV, JSON, code)"
          disabled={isLoading || isUploading}
        >
          <Paperclip className="w-3.5 h-3.5" />
        </button>

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
          placeholder={isUploading
            ? 'Uploading files...'
            : attachedFiles.length > 0
              ? t('describeAnalysis', 'Describe what to analyze...')
              : t('askAnything', 'Ask anything...')}
          className="flex-1 bg-transparent border-none outline-none px-2 py-2.5 sm:py-1.5 text-sm sm:text-xs"
          style={{ color: C.text }}
          disabled={isLoading || isUploading}
        />

        {/* Send button */}
        <button
          onClick={() => handleSendMessage()}
          disabled={(!input.trim() && attachedFiles.length === 0) || isLoading || isUploading}
          className="p-2 rounded-lg transition-all"
          style={(input.trim() || attachedFiles.length > 0) && !isLoading && !isUploading
            ? { background: C.gold, color: C.bg }
            : { background: C.s3, color: C.t3 }
          }
        >
          {isLoading || isUploading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
    </div>
  );

  // Render the controls bar (model badge, agent toggle, canvas toggle)
  const renderControlsBar = () => (
    <div className="flex items-center justify-center gap-2 mt-3">
      {/* Model Switch */}
      <button
        onClick={() => setSelectedModel(prev => prev === 'gemini' ? 'claude' : 'gemini')}
        className="px-2 py-1 rounded-md text-[10px] font-medium transition-colors cursor-pointer"
        style={selectedModel === 'gemini'
          ? { background: 'rgba(66, 133, 244, 0.15)', border: '1px solid rgba(66, 133, 244, 0.3)', color: '#4285f4' }
          : { background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)', color: '#a78bfa' }
        }
        title={`Switch to ${selectedModel === 'gemini' ? 'Claude Opus' : 'Gemini 3.1 Pro'}`}
      >
        {selectedModel === 'gemini' ? 'Gemini 3.1 Pro' : 'Claude Opus'}
      </button>

      {/* Agent Mode Toggle */}
      <button
        onClick={() => setUseAgentMode(!useAgentMode)}
        className="flex items-center gap-0.5 px-1.5 py-1 rounded-md text-[10px] transition-colors"
        style={useAgentMode
          ? { background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }
          : { background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }
        }
      >
        <Zap className="w-2.5 h-2.5" />
        Agent
      </button>

      {/* Canvas Toggle */}
      <button
        onClick={() => {
          setShowCanvas(!showCanvas);
          // Mutually exclusive with pattern legend (Selma)
          if (!showCanvas) setShowPatternLegend(false);
        }}
        className="p-1 rounded-md transition-colors"
        style={showCanvas
          ? { background: C.g08, color: C.gold }
          : { background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }
        }
        title={showCanvas ? t('hideCanvas', 'Hide Canvas') : t('showCanvas', 'Show Canvas')}
      >
        {showCanvas ? <PanelRightClose className="w-3.5 h-3.5" /> : <PanelRightOpen className="w-3.5 h-3.5" />}
      </button>

      {/* Pattern Legend Toggle (only in analytics mode) */}
      {analyticsMode && (
        <button
          onClick={() => {
            setShowPatternLegend(!showPatternLegend);
            // Mutually exclusive with canvas (Selma)
            if (!showPatternLegend) setShowCanvas(false);
          }}
          className="flex items-center gap-0.5 px-1.5 py-1 rounded-md text-[10px] transition-colors"
          style={showPatternLegend
            ? { background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }
            : { background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }
          }
          title={showPatternLegend ? 'Hide patterns' : 'Show patterns'}
        >
          <BarChart2 className="w-2.5 h-2.5" />
          Patterns{sessionPatterns.length > 0 ? ` (${sessionPatterns.length})` : ''}
        </button>
      )}

      {/* Language Selector */}
      <div className="relative language-dropdown">
        <button
          onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          className="flex items-center gap-0.5 px-1.5 py-1 rounded-md text-[10px] transition-colors"
          style={language !== 'en'
            ? { background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }
            : { background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }
          }
          title={t('languageSelector', 'Language')}
        >
          <Globe className="w-2.5 h-2.5" />
          <span className="uppercase">{language}</span>
        </button>
        {showLanguageDropdown && (
          <div className="absolute left-0 bottom-full mb-1 w-40 rounded-md shadow-xl z-50 py-0.5 max-h-60 overflow-y-auto" style={{ background: C.s2, border: `1px solid ${C.border}` }}>
            {(showAllLanguages ? supportedLanguages : TOP_LANGUAGES).map((lang) => (
              <button
                key={lang.code}
                onClick={() => { setLanguage(lang.code); setShowLanguageDropdown(false); setShowAllLanguages(false); }}
                className="w-full text-left px-2 py-1.5 text-[10px] transition-colors flex items-center gap-1.5"
                style={language === lang.code
                  ? { background: C.g08, color: C.gold }
                  : { color: C.t2 }
                }
              >
                <span>{lang.flag}</span>
                <span>{lang.nativeName}</span>
              </button>
            ))}
            {!showAllLanguages && (
              <button
                onClick={() => setShowAllLanguages(true)}
                className="w-full text-left px-2 py-1.5 text-[10px] transition-colors"
                style={{ color: C.gold }}
              >
                {t('moreLanguages', 'More languages...')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Export + Clear Chat - only show when there are messages */}
      {messages.length > 0 && (
        <>
          <button
            onClick={handleExportConversation}
            disabled={isExporting}
            className="p-1 rounded-md transition-colors"
            style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }}
            title="Export conversation as PDF"
          >
            {isExporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={handleClearChat}
            className="p-1 rounded-md transition-colors"
            style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }}
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </>
      )}
    </div>
  );

  // Scroll to a specific message turn (for pattern click navigation)
  const handlePatternClick = useCallback((sourceTurnId) => {
    if (!sourceTurnId) return;
    // Find message element by turn index and scroll into view
    const messageEls = document.querySelectorAll('[data-turn-id]');
    for (const el of messageEls) {
      if (el.dataset.turnId === String(sourceTurnId)) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Flash highlight
        el.style.outline = `2px solid ${C.gold}`;
        el.style.outlineOffset = '2px';
        setTimeout(() => { el.style.outline = 'none'; }, 2000);
        break;
      }
    }
  }, []);

  // Extract patterns from active analytics session metadata
  const sessionPatterns = useMemo(() => {
    if (!analyticsMode || !analytics.activeSession?.metadata?.patterns) return [];
    return analytics.activeSession.metadata.patterns;
  }, [analyticsMode, analytics.activeSession]);

  // Filtered sessions for search
  const filteredSessions = useMemo(() => {
    if (!sessionSearch.trim()) return analytics.sessions;
    const q = sessionSearch.toLowerCase();
    return analytics.sessions.filter(s =>
      (s.title || '').toLowerCase().includes(q) ||
      (s.sport_context?.sportCategory || '').toLowerCase().includes(q)
    );
  }, [analytics.sessions, sessionSearch]);

  // Determine which side panel to show (mutually exclusive per Selma)
  const showPatternPanel = analyticsMode && showPatternLegend && !showCanvas;

  return (
    <div className="flex h-full overflow-hidden bg-transparent">
      {/* Main Chat Panel */}
      <div className={`relative flex flex-col flex-1 ${showCanvas ? 'md:w-[60%] w-full' : 'w-full'} transition-all duration-300`}>
        {/* Context Bar */}
        <div
          className="flex items-center flex-wrap gap-1.5 px-3 py-2 overflow-x-auto"
          style={{ borderBottom: `1px solid ${C.border}`, background: C.s1, fontSize: 10, color: C.t2, WebkitOverflowScrolling: 'touch' }}
        >
          <span style={{ color: C.gold, fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
            {chatContext.isBaseball ? 'BASEBALL' : 'BASKETBALL'}
          </span>
          {chatContext.leagueCode && (
            <span className="px-1.5 py-0.5 rounded" style={{ background: C.s2, color: C.t2 }}>
              {chatContext.leagueCode}
            </span>
          )}
          {chatContext.leagueName && chatContext.leagueName !== chatContext.leagueCode && (
            <span className="px-1.5 py-0.5 rounded" style={{ background: C.s2, color: C.t2 }}>
              {chatContext.leagueName}
            </span>
          )}
          {chatContext.gender && (
            <span className="px-1.5 py-0.5 rounded capitalize" style={{ background: C.s2, color: C.t2 }}>
              {chatContext.gender}
            </span>
          )}
          {chatContext.conference && (
            <span className="px-1.5 py-0.5 rounded" style={{ background: C.s2, color: C.t2 }}>
              {chatContext.conference}
            </span>
          )}
          {teamName && (
            <span className="px-1.5 py-0.5 rounded" style={{ background: C.g08, color: C.gold }}>
              {teamName}
            </span>
          )}
          <span style={{ color: C.t3 }}>
            {chatContext.isBaseball ? 'Statcast + ADK + Gemini' : 'ADK + BigQuery + Vertex + Spanner'}
          </span>

          {/* Analytics mode toggle (Ned: place in context bar near sport/team selector) */}
          <button
            onClick={() => {
              const next = !analyticsMode;
              setAnalyticsMode(next);
              if (next && !analytics.activeSession) {
                // Auto-create a new session when enabling analytics mode
                analytics.createSession();
              }
              // Mutually exclusive with canvas (Selma)
              if (next && showPatternLegend) {
                setShowCanvas(false);
              }
            }}
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-md ml-auto transition-colors"
            style={analyticsMode
              ? { background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold, fontSize: 10, fontWeight: 500 }
              : { background: C.s2, border: `1px solid ${C.border}`, color: C.t3, fontSize: 10, fontWeight: 500 }
            }
            title={analyticsMode ? 'Disable analytics mode' : 'Enable analytics mode — sessions are saved automatically'}
          >
            <BarChart2 className="w-3 h-3" />
            Analytics
          </button>

          {/* Session picker dropdown (when analytics mode active) */}
          {analyticsMode && (
            <div className="relative session-picker-dropdown">
              <button
                onClick={() => { setShowSessionPicker(!showSessionPicker); if (showSessionPicker) setSessionSearch(''); }}
                className="flex items-center gap-1 px-1.5 py-0.5 rounded-md transition-colors"
                style={{
                  background: C.s2,
                  border: `1px solid ${C.border}`,
                  color: C.t2,
                  fontSize: 10,
                }}
              >
                <Clock className="w-3 h-3" />
                <span className="max-w-[100px] truncate">
                  {analytics.activeSession?.title || 'New Session'}
                </span>
                <ChevronDown className="w-2.5 h-2.5" />
              </button>

              {showSessionPicker && (
                <div
                  className="absolute left-0 top-full mt-1 w-64 rounded-lg shadow-xl z-50 overflow-hidden"
                  style={{ background: C.s2, border: `1px solid ${C.border}` }}
                >
                  {/* New session button */}
                  <button
                    onClick={async () => {
                      resetChat();
                      await analytics.createSession();
                      setShowSessionPicker(false);
                    }}
                    className="w-full flex items-center gap-1.5 px-3 py-2 text-left transition-colors"
                    style={{ color: C.gold, fontSize: 11, borderBottom: `1px solid ${C.borderSubtle}` }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.s3; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <Plus className="w-3 h-3" />
                    New Session
                  </button>

                  {/* Session search */}
                  {analytics.sessions.length > 3 && (
                    <div className="px-2 py-1.5" style={{ borderBottom: `1px solid ${C.borderSubtle}` }}>
                      <div className="flex items-center gap-1.5 px-2 py-1 rounded"
                           style={{ background: C.s2, border: `1px solid ${C.border}` }}>
                        <Search className="w-3 h-3 shrink-0" style={{ color: C.t3 }} />
                        <input
                          type="text"
                          value={sessionSearch}
                          onChange={e => setSessionSearch(e.target.value)}
                          placeholder="Search sessions..."
                          className="w-full bg-transparent outline-none"
                          style={{ fontSize: 10, color: C.text }}
                        />
                        {sessionSearch && (
                          <button onClick={() => setSessionSearch('')} style={{ color: C.t3 }}>
                            <X className="w-2.5 h-2.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Session list */}
                  {analytics.loading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: C.t3 }} />
                    </div>
                  ) : filteredSessions.length === 0 ? (
                    <div className="px-3 py-3 text-center" style={{ color: C.t3, fontSize: 10 }}>
                      {sessionSearch ? 'No sessions match your search' : 'Your analytics sessions are saved automatically'}
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto">
                      {filteredSessions.map((session) => (
                        <div
                          key={session.session_id}
                          className="group relative"
                        >
                          <button
                            onClick={async () => {
                              const loaded = await analytics.loadSession(session.session_id);
                              if (!loaded) {
                                console.warn('[AskSLAMAI] Session load failed, creating new session');
                                await analytics.createSession();
                              }
                              setShowSessionPicker(false);
                            }}
                            className="w-full flex items-center justify-between px-3 py-1.5 text-left transition-colors"
                            style={{
                              background: analytics.activeSession?.session_id === session.session_id ? C.g08 : 'transparent',
                              color: C.text,
                              fontSize: 11,
                            }}
                            onMouseEnter={e => { if (analytics.activeSession?.session_id !== session.session_id) e.currentTarget.style.background = C.s3; }}
                            onMouseLeave={e => { if (analytics.activeSession?.session_id !== session.session_id) e.currentTarget.style.background = 'transparent'; }}
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              {session.sport_context?.sportCategory && (
                                <span className="px-1 py-0.5 rounded text-[8px] uppercase shrink-0"
                                      style={{ background: C.g08, color: C.gold }}>
                                  {session.sport_context.sportCategory === 'baseball' ? 'MLB'
                                    : session.sport_context.sportCategory === 'basketball' ? 'NBA'
                                    : session.sport_context.sportCategory === 'football' ? 'NFL'
                                    : session.sport_context.sportCategory === 'hockey' ? 'NHL'
                                    : session.sport_context.sportCategory.toUpperCase().slice(0, 3)}
                                </span>
                              )}
                              <span className="truncate">{session.title || 'Untitled Session'}</span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0" style={{ fontSize: 9, color: C.t3 }}>
                              {session.turn_count > 0 && <span>{session.turn_count}t</span>}
                              <span>
                                {new Date(session.updated_at || session.created_at).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                              </span>
                            </div>
                          </button>
                          {/* Delete button — visible on hover */}
                          <button
                            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                            style={{ color: C.t3 }}
                            title="Delete session"
                            onMouseEnter={e => { e.currentTarget.style.color = C.dev; }}
                            onMouseLeave={e => { e.currentTarget.style.color = C.t3; }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (window.confirm('Delete this session?')) {
                                await analytics.deleteSession(session.session_id);
                              }
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Messages Area - full height with proper overflow */}
        <div className="flex-1 overflow-y-auto px-3 pt-2 pb-2">
          {messages.length === 0 ? (
            // Empty state with prompts, input, and controls - all together
            <div className="flex flex-col items-center justify-center h-full max-w-md mx-auto px-2 sm:px-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ background: C.g08, border: `1px solid ${C.goldDim}`, color: C.gold }}
              >
                <Sparkles className="w-5 h-5" />
              </div>
              <h2 style={{ fontSize: 15, fontWeight: 200, marginBottom: 4, color: C.text }}>
                {t('whatCanIHelp', 'What can I help with?')}
              </h2>
              <p style={{ fontSize: 11, textAlign: 'center', marginBottom: 16, color: C.t2 }}>
                {t('analyzeData', 'Ask questions, analyze data, or get strategic insights')}
              </p>

              {/* Prompt Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full mb-4">
                {effectivePrompts.map((prompt) => (
                  <button
                    key={prompt.id}
                    onClick={() => handlePromptClick(prompt)}
                    className="flex items-center gap-2 p-2.5 rounded-lg text-left transition-all text-xs"
                    style={{ background: C.s2, border: `1px solid ${C.border}`, color: C.t2 }}
                    onMouseEnter={e => { e.currentTarget.style.background = C.s3; e.currentTarget.style.borderColor = C.goldDim; }}
                    onMouseLeave={e => { e.currentTarget.style.background = C.s2; e.currentTarget.style.borderColor = C.border; }}
                  >
                    <span className="text-base">{prompt.icon}</span>
                    <span className="line-clamp-2">{prompt.text}</span>
                  </button>
                ))}
              </div>

              {/* Analytics mode recording indicator */}
              {analyticsMode && (
                <div className="flex items-center gap-1.5 px-3 py-1" style={{ fontSize: 9, color: C.t3 }}>
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.dev }} />
                  Session recording
                </div>
              )}

              {/* Input bar - right under prompts */}
              {renderInputBar()}

              {/* Controls bar - under input */}
              {renderControlsBar()}
            </div>
          ) : (
            // Messages list
            <div className="max-w-xl mx-auto space-y-4 px-1 sm:px-0">
              {messages.map((message, idx) => (
                <div key={idx} data-turn-id={String(idx)}>
                  <MessageBubble
                    message={message}
                    showTrace={useAgentMode}
                    onCitationClick={handleCitationClick}
                  />
                </div>
              ))}

              {/* Live agent trace during streaming - shows pipeline steps */}
              {isLoading && (
                <div className="ml-2">
                  <AgentTraceInline
                    steps={traceSteps}
                    isStreaming={isLoading}
                    agentState={agentState}
                    statusMessage={statusMessage}
                    currentAgentName={currentAgentName}
                  />
                </div>
              )}

              {/* MCP App cards during streaming (appear as tools finish) */}
              {isLoading && artifacts && artifacts.filter(a => a.type === 'mcp_app').length > 0 && (
                <div className="space-y-2 ml-8">
                  {artifacts.filter(a => a.type === 'mcp_app').map((app, i) => (
                    <div
                      key={`stream-mcp-${i}`}
                      className="rounded-xl overflow-hidden"
                      style={{ border: `1px solid ${C.goldDim}`, background: C.s1 }}
                    >
                      <div
                        className="flex items-center gap-2 px-3 py-1.5"
                        style={{
                          background: `linear-gradient(135deg, ${C.s2} 0%, ${C.s1} 100%)`,
                          borderBottom: `1px solid ${C.border}`,
                        }}
                      >
                        <span
                          className="w-5 h-5 rounded flex items-center justify-center"
                          style={{ background: C.g08, color: C.gold, fontSize: 10 }}
                        >◈</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>
                          {app.title || 'Interactive View'}
                        </span>
                        <span
                          className="ml-auto"
                          style={{
                            fontSize: 9, fontWeight: 600, padding: '1px 5px',
                            borderRadius: 4, background: C.g08, color: C.gold,
                            textTransform: 'uppercase', letterSpacing: 0.5,
                          }}
                        >MCP APP</span>
                      </div>
                      <MCPAppRenderer
                        resourceUri={app.resourceUri}
                        toolName={app.toolName}
                        toolInput={app.toolInput || {}}
                        toolResult={app.toolResult || {}}
                        height={380}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Streaming response (appears below trace as text arrives) */}
              {isLoading && textContent && (
                <MessageBubble
                  message={{ role: 'assistant', content: textContent }}
                  showTrace={false}

                />
              )}

              {/* Error display */}
              {error && (
                <div className="p-4 rounded-lg" style={{ background: `${C.dev}15`, border: `1px solid ${C.dev}40`, color: C.dev }}>
                  {error}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area - fixed at bottom (only when messages exist, empty state has its own input) */}
        {messages.length > 0 && (
          <div className="flex-shrink-0 px-3 py-2 bg-transparent">
            <div className="max-w-xl mx-auto">
              {/* Analytics mode recording indicator */}
              {analyticsMode && (
                <div className="flex items-center gap-1.5 px-3 py-1" style={{ fontSize: 9, color: C.t3 }}>
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: C.dev }} />
                  Session recording
                </div>
              )}
              {renderInputBar()}
              {renderControlsBar()}
            </div>
          </div>
        )}
      </div>

      {/* Canvas Panel (mutually exclusive with PatternLegend per Selma) */}
      {showCanvas && !showPatternPanel && (
        <div
          className="md:relative md:w-[40%] fixed inset-0 z-50 md:z-auto"
          style={{ borderLeft: window.innerWidth >= 768 ? `1px solid ${C.border}` : 'none' }}
        >
          {/* Mobile backdrop */}
          <div
            className="md:hidden absolute inset-0 bg-black/60"
            onClick={() => setShowCanvas(false)}
          />
          <div className="md:h-full h-full w-full md:w-auto relative" style={{ background: C.bg }}>
            <ChatCanvas
              artifacts={artifacts}
              isOpen={showCanvas}
              onClose={() => setShowCanvas(false)}
              onTogglePin={toggleArtifactPin}
              onRemove={removeArtifact}
            />
          </div>
        </div>
      )}

      {/* Pattern Legend Panel (analytics mode, mutually exclusive with Canvas) */}
      {showPatternPanel && (
        <PatternLegend
          patterns={sessionPatterns}
          onPatternClick={handlePatternClick}
          onClose={() => setShowPatternLegend(false)}
        />
      )}

      {/* Source Drawer (citations detail panel) */}
      <SourceDrawer
        citations={drawerCitations}
        isOpen={showSourceDrawer}
        onClose={() => setShowSourceDrawer(false)}
      />
    </div>
  );
}

// Named exports
export { MessageBubble, DEFAULT_PROMPTS };
